// ─── Actor fields — three per-surface subsets ─────────────────────────────────
// Each subset matches what the corresponding surface actually exposes (per the
// migration remap). The .value rows share the description of their .formatted
// sibling; the placeholder column distinguishes them.
// Full set — used by quote `contractee.*`, `contractor.*`, `endClient.*`
// (legacy quote picker exposed every actor field).
const QUOTE_ACTOR_FIELDS = [
    { suffix: "name", descriptionKey: "Name of person or Company name" },
    {
        suffix: "formalName",
        descriptionKey: "Name of person with salutation, or Company name",
    },
    {
        suffix: "shortFormalName",
        descriptionKey: "Name of person with salutation and family name only, or Company name",
    },
    { suffix: "salutation.formatted", descriptionKey: "Salutation" },
    { suffix: "salutation.value", descriptionKey: "Salutation" },
    { suffix: "firstName", descriptionKey: "First name" },
    { suffix: "familyName", descriptionKey: "Family name" },
    { suffix: "phone", descriptionKey: "Phone" },
    { suffix: "email", descriptionKey: "Email" },
    { suffix: "number", descriptionKey: "Number" },
    { suffix: "address.formatted", descriptionKey: "Address" },
    // Address sub-components share the description of address.formatted (matches
    // the old picker's grouped-row display).
    { suffix: "address.streetAndNumber", descriptionKey: "Address" },
    { suffix: "address.city", descriptionKey: "Address" },
    { suffix: "address.postalCode", descriptionKey: "Address" },
    { suffix: "address.countryCode", descriptionKey: "Address" },
    {
        suffix: "contactPerson.salutation.formatted",
        descriptionKey: "Contact person salutation",
    },
    {
        suffix: "contactPerson.salutation.value",
        descriptionKey: "Contact person salutation",
    },
    {
        suffix: "contactPerson.firstName",
        descriptionKey: "Contact person first name",
    },
    {
        suffix: "contactPerson.familyName",
        descriptionKey: "Contact person family name",
    },
    { suffix: "contactPerson.name", descriptionKey: "Contact person name" },
    {
        suffix: "contactPerson.formalName",
        descriptionKey: "Contact person name with salutation",
    },
    {
        suffix: "contactPerson.shortFormalName",
        descriptionKey: "Contact person name with salutation and family name only",
    },
];
// Invoice / TimeReport `client.*` — only what the angle-bracket remap migrated
// (`<client name|firstname|lastname|salutation>`).
const CLIENT_FIELDS = [
    { suffix: "name", descriptionKey: "Name of person or Company name" },
    { suffix: "firstName", descriptionKey: "First name" },
    { suffix: "familyName", descriptionKey: "Family name" },
    { suffix: "salutation.formatted", descriptionKey: "Salutation" },
    { suffix: "salutation.value", descriptionKey: "Salutation" },
];
// Email `recipient.*` — the legacy CRM-email picker's full set
// (`{{client name|firstname|lastname|salutation|email address|phone number}}`).
const RECIPIENT_FIELDS = [
    { suffix: "name", descriptionKey: "Name of person or Company name" },
    { suffix: "firstName", descriptionKey: "First name" },
    { suffix: "familyName", descriptionKey: "Family name" },
    { suffix: "salutation.formatted", descriptionKey: "Salutation" },
    { suffix: "salutation.value", descriptionKey: "Salutation" },
    { suffix: "phone", descriptionKey: "Phone" },
    { suffix: "email", descriptionKey: "Email" },
];
function makeQuoteActorEntries(entity, channels) {
    return QUOTE_ACTOR_FIELDS.map(({ suffix, descriptionKey }) => ({
        id: `${entity}.${suffix}`,
        descriptionKey,
        channels,
    }));
}
function makeClientActorEntries() {
    return CLIENT_FIELDS.map(({ suffix, descriptionKey }) => ({
        id: `client.${suffix}`,
        descriptionKey,
        channels: ["document"],
    }));
}
function makeRecipientActorEntries() {
    return RECIPIENT_FIELDS.map(({ suffix, descriptionKey }) => ({
        id: `recipient.${suffix}`,
        descriptionKey,
        channels: ["email"],
    }));
}
// ─── Document header fields ───────────────────────────────────────────────────
// Applied to quote-legacy `document`, plus `invoice` (which uses .title only).
const QUOTE_DOCUMENT_FIELDS = [
    { suffix: "name", descriptionKey: "Document name" },
    { suffix: "number", descriptionKey: "Document number" },
    { suffix: "publishDate.formatted", descriptionKey: "Document publish date" },
    { suffix: "publishDate.value", descriptionKey: "Document publish date" },
    { suffix: "validUntil.formatted", descriptionKey: "Document valid until" },
    { suffix: "validUntil.value", descriptionKey: "Document valid until" },
    {
        suffix: "earliestWorkStart.formatted",
        descriptionKey: "Document earliest work start",
    },
    {
        suffix: "earliestWorkStart.value",
        descriptionKey: "Document earliest work start",
    },
    {
        suffix: "workDuration.formatted",
        descriptionKey: "Document work duration",
    },
    {
        suffix: "workDuration.value",
        descriptionKey: "Document work duration",
    },
];
// Only the quote-legacy `document` entity uses the full QUOTE_DOCUMENT_FIELDS
// shape — invoice/PO/TR/ML expose nothing or just `invoice.title` per the remap.
function makeQuoteDocumentEntries(channels) {
    return QUOTE_DOCUMENT_FIELDS.map(({ suffix, descriptionKey }) => ({
        id: `document.${suffix}`,
        descriptionKey,
        channels,
    }));
}
// ─── The registry ─────────────────────────────────────────────────────────────
export const PLACEHOLDER_REGISTRY = [
    // ─── Quote-legacy document header (channel: document + email for QUOTE-context emails)
    ...makeQuoteDocumentEntries(["document", "email"]),
    // ─── Invoice document header (channel: document only)
    {
        id: "invoice.title",
        descriptionKey: "The title of the invoice",
        channels: ["document"],
    },
    // ─── Actors (full field set across every actor entity)
    ...makeQuoteActorEntries("contractee", ["document"]),
    ...makeQuoteActorEntries("contractor", ["document"]),
    ...makeQuoteActorEntries("endClient", ["document"]),
    ...makeClientActorEntries(),
    ...makeRecipientActorEntries(),
    // ─── Invitee (member-invite email; only the 3 ids in the migration remap)
    {
        id: "invitee.name",
        descriptionKey: 'The first and last name of the member, e.g. "John Smith"',
        channels: ["email"],
    },
    {
        id: "invitee.firstName",
        descriptionKey: 'The first name of the member, e.g. "John"',
        channels: ["email"],
    },
    {
        id: "invitee.familyName",
        descriptionKey: 'The last name of the member, e.g. "Smith"',
        channels: ["email"],
    },
    // ─── Supplier (purchase order)
    {
        id: "supplier.name",
        descriptionKey: "The name of the supplier",
        channels: ["document"],
    },
    // ─── Organisation (shared — the rendering org)
    {
        id: "organisation.name",
        descriptionKey: "The name of your organisation",
        channels: ["document", "email"],
    },
    // ─── User (shared — the logged-in user; only the id in the migration remap)
    {
        id: "user.name",
        descriptionKey: 'Your full name, e.g. "John Doe"',
        channels: ["document", "email"],
    },
    // ─── Project (used in document AND email scopes that have project)
    {
        id: "project.name",
        descriptionKey: "Project name",
        channels: ["document", "email"],
    },
    {
        id: "project.number",
        descriptionKey: "Project number",
        channels: ["document", "email"],
    },
    {
        id: "project.address.formatted",
        descriptionKey: "Project address",
        channels: ["document", "email"],
    },
    // Project address sub-components share the description of address.formatted.
    {
        id: "project.address.streetAndNumber",
        descriptionKey: "Project address",
        channels: ["document", "email"],
    },
    {
        id: "project.address.city",
        descriptionKey: "Project address",
        channels: ["document", "email"],
    },
    {
        id: "project.address.postalCode",
        descriptionKey: "Project address",
        channels: ["document", "email"],
    },
    {
        id: "project.address.countryCode",
        descriptionKey: "Project address",
        channels: ["document", "email"],
    },
    {
        id: "project.budget.formatted",
        descriptionKey: "Project budget",
        channels: ["document", "email"],
    },
    {
        id: "project.budget.value",
        descriptionKey: "Project budget",
        channels: ["document", "email"],
    },
    {
        id: "project.earliestStart.formatted",
        descriptionKey: "Project earliest start",
        channels: ["document", "email"],
    },
    {
        id: "project.earliestStart.value",
        descriptionKey: "Project earliest start",
        channels: ["document", "email"],
    },
    {
        id: "project.deadline.formatted",
        descriptionKey: "Project deadline",
        channels: ["document", "email"],
    },
    {
        id: "project.deadline.value",
        descriptionKey: "Project deadline",
        channels: ["document", "email"],
    },
    {
        id: "project.leadId",
        descriptionKey: "Lead identifier of the project",
        channels: ["email"],
    },
    {
        id: "project.contracteeInputPageUrl",
        descriptionKey: "URL to the page for providing project info & uploading images",
        channels: ["email"],
    },
    // ─── Requirement (requirement-context email; only the id in the migration remap)
    {
        id: "requirement.contracteeInputPageUrl",
        descriptionKey: "URL to the page for providing project info & uploading images",
        channels: ["email"],
    },
    // ─── Calculation (quote document totals)
    {
        id: "calculation.subTotal.formatted",
        descriptionKey: "Document subtotal",
        channels: ["document"],
    },
    {
        id: "calculation.subTotal.value",
        descriptionKey: "Document subtotal",
        channels: ["document"],
    },
    {
        id: "calculation.netTotal.formatted",
        descriptionKey: "Document net price",
        channels: ["document"],
    },
    {
        id: "calculation.netTotal.value",
        descriptionKey: "Document net price",
        channels: ["document"],
    },
    {
        id: "calculation.total.formatted",
        descriptionKey: "Document total",
        channels: ["document"],
    },
    {
        id: "calculation.total.value",
        descriptionKey: "Document total",
        channels: ["document"],
    },
    {
        id: "calculation.discount.formatted",
        descriptionKey: "Document discount",
        channels: ["document"],
    },
    {
        id: "calculation.discount.value",
        descriptionKey: "Document discount",
        channels: ["document"],
    },
    {
        id: "calculation.discountRate.formatted",
        descriptionKey: "Document discount percentage",
        channels: ["document"],
    },
    {
        id: "calculation.discountRate.value",
        descriptionKey: "Document discount percentage",
        channels: ["document"],
    },
    {
        id: "calculation.vat.formatted",
        descriptionKey: "Document VAT",
        channels: ["document"],
    },
    {
        id: "calculation.vat.value",
        descriptionKey: "Document VAT",
        channels: ["document"],
    },
    {
        id: "calculation.vatRate.formatted",
        descriptionKey: "Document VAT percentage",
        channels: ["document"],
    },
    {
        id: "calculation.vatRate.value",
        descriptionKey: "Document VAT percentage",
        channels: ["document"],
    },
    // ─── Main product (quote document)
    {
        id: "mainProduct.title",
        descriptionKey: "Product title",
        channels: ["document"],
    },
    {
        id: "mainProduct.category",
        descriptionKey: "Product category",
        channels: ["document"],
    },
    {
        id: "mainProduct.articleNumber",
        descriptionKey: "Product article number",
        channels: ["document"],
    },
    {
        id: "mainProduct.manufacturerArticleNumber",
        descriptionKey: "Product manufacturer article number",
        channels: ["document"],
    },
    {
        id: "mainProduct.brand",
        descriptionKey: "Product brand",
        channels: ["document"],
    },
    {
        id: "mainProduct.brandLine",
        descriptionKey: "Product brand line",
        channels: ["document"],
    },
    {
        id: "mainProduct.productType",
        descriptionKey: "Product type",
        channels: ["document"],
    },
    {
        id: "mainProduct.supplier.title",
        descriptionKey: "Product supplier",
        channels: ["document"],
    },
    // Dynamic property keys — id uses `<prop_key>` placeholder for documentation;
    // the actual customer template substitutes a real property key.
    {
        id: "mainProduct.properties.<prop_key>.formatted",
        descriptionKey: "Main product item property",
        channels: ["document"],
    },
    {
        id: "mainProduct.properties.<prop_key>.value",
        descriptionKey: "Main product item property",
        channels: ["document"],
    },
    // ─── Root item (quote document dynamic properties)
    {
        id: "rootItem.properties.<prop_key>.formatted",
        descriptionKey: "Root item property",
        channels: ["document"],
    },
    {
        id: "rootItem.properties.<prop_key>.value",
        descriptionKey: "Root item property",
        channels: ["document"],
    },
];
// The entity an id belongs to is its first path segment (`project.name` →
// `project`). Ids are `LeafPaths<PlaceholderContext>`, so that segment is always
// a `PlaceholderEntity` by construction — the assertion restates that invariant
// rather than assuming an unchecked value.
export function placeholderEntity(id) {
    return id.slice(0, id.indexOf("."));
}
export function availablePlaceholders(scope) {
    const entitiesInScope = new Set(scope.entitiesInScope);
    return PLACEHOLDER_REGISTRY.filter(entry => {
        if (!entry.channels.includes(scope.channel))
            return false;
        return entitiesInScope.has(placeholderEntity(entry.id));
    });
}
// ─── Grouping (picker display) ───────────────────────────────────────────────
// Pickers show placeholders under collapsible group headers. The group is
// derived from the entry's entity (the id's first path segment). Most entities
// map 1:1; several fold together so related placeholders sit under one header:
//   - `organisation` + `user`  → "From" (both describe who the doc/email is
//     from — the rendering org and the logged-in user)
//   - everything that's part of the document being rendered
//     (`invoice`, `requirement`, `calculation`, `mainProduct`, `rootItem`)
//     → "Document", alongside the quote-legacy `document` header fields.
//
// Declaration order doubles as the canonical display order of the groups (see
// `CANONICAL_GROUP_ORDER`): the first appearance of each group id below fixes
// where that group renders, so a group sits in the same place across every
// scope regardless of which of its (possibly folded) entities happen to be in
// scope.
const GROUP_BY_ENTITY = {
    contractee: { id: "contractee", descriptionKey: "Contractee" },
    contractor: { id: "contractor", descriptionKey: "Contractor" },
    endClient: { id: "endClient", descriptionKey: "End client" },
    client: { id: "client", descriptionKey: "Client" },
    recipient: { id: "recipient", descriptionKey: "Recipient" },
    invitee: { id: "invitee", descriptionKey: "Member" },
    supplier: { id: "supplier", descriptionKey: "Supplier" },
    organisation: { id: "from", descriptionKey: "From" },
    user: { id: "from", descriptionKey: "From" },
    document: { id: "document", descriptionKey: "Document" },
    invoice: { id: "document", descriptionKey: "Document" },
    requirement: { id: "document", descriptionKey: "Document" },
    calculation: { id: "document", descriptionKey: "Document" },
    mainProduct: { id: "document", descriptionKey: "Document" },
    rootItem: { id: "document", descriptionKey: "Document" },
    project: { id: "project", descriptionKey: "Project" },
};
// Canonical group display order — the distinct group ids in the order they
// first appear in `GROUP_BY_ENTITY`. Used to give groups a stable, scope-
// independent position (folded groups like "document" would otherwise jump
// around depending on which member entity a given scope surfaces first).
const CANONICAL_GROUP_ORDER = [
    ...new Set(Object.values(GROUP_BY_ENTITY).map(g => g.id)),
];
// Like `availablePlaceholders`, but bucketed into display groups keyed by group
// id. Groups are emitted in `CANONICAL_GROUP_ORDER`; entries within a group keep
// registry (logical) order. No alphabetical sorting.
export function availablePlaceholdersGrouped(scope) {
    const grouped = availablePlaceholders(scope).reduce((groups, entry) => {
        const { id, descriptionKey } = GROUP_BY_ENTITY[placeholderEntity(entry.id)];
        const group = groups[id];
        if (group) {
            group.entries.push(entry);
        }
        else {
            groups[id] = { descriptionKey, entries: [entry] };
        }
        return groups;
    }, {});
    const ordered = {};
    for (const id of CANONICAL_GROUP_ORDER) {
        const group = grouped[id];
        if (group)
            ordered[id] = group;
    }
    return ordered;
}
//# sourceMappingURL=registry.js.map